// NOTE: This is a container that relies on state being passed in from outside,
// usually controlled by state in the parent component. I think this is less flexible
// than storing dialog state in redux (where we have more control and visibility into
// which dialogs are open), so I'm hoping that we gradually transition to using the `../ReduxContainer`
// component instead

import { ReactNode } from 'react';
import FocusTrap from 'focus-trap-react';
import closeButton from 'public/static/images/paid_landing/close.svg';
import { colors, screen } from 'components/styles/variables';
import useLockedScreen from 'lib/hooks/useLockedScreen';
import useModalExiting from 'lib/hooks/useModalExiting';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import classnames from 'classnames';

interface Props {
  hide(): void;
  isShown: boolean;
  render(): ReactNode;
  displayCloseButton?: boolean;
  className?: string;
}

export default function Container({
  isShown,
  render,
  hide,
  displayCloseButton = true,
  className,
}: Props): JSX.Element | null {
  const dialogRef = useModalExiting(hide);

  useLockedScreen(isShown);
  const { isMobile, isTablet } = useDeviceScreen();

  if (!isShown) {
    return null;
  }

  return (
    <div className="dialog-container">
      <div className="overlay" />
      <div className="main" data-testid="outside">
        {!isMobile && !isTablet && (
          <div aria-live="polite" className="escape-announcement">
            Press the escape key to exit the dialog
          </div>
        )}
        <FocusTrap focusTrapOptions={{ fallbackFocus: '.dialog' }}>
          <div role="dialog" className={classnames('dialog', className)} ref={dialogRef}>
            {displayCloseButton && <button className="close" onClick={hide} />}
            {render()}
          </div>
        </FocusTrap>
      </div>
      <style jsx>
        {`
          .dialog-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 9999;
            overflow: scroll;
            background-color: white;
          }

          .overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: black;
            opacity: 0.5;
          }

          .main {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
          }

          .dialog {
            user-select: none;
            background-color: white;
            position: relative;
            font-size: 18px;
            width: 100%;
            height: 100%;
            border-radius: 0;
            box-shadow: 0 8px 64px 0 rgba(0, 0, 0, 0.25);
          }

          @media (min-width: ${screen.smMin}) {
            .dialog {
              width: 435px;
              height: auto;
              border-radius: 5px;
              overflow: hidden;
            }

            .dialog-container {
              background-color: transparent;
            }

            .close {
              visibility: hidden;
            }
          }

          .close {
            position: absolute;
            right: 12px;
            top: 15px;
            height: 30px;
            width: 30px;
            border: 0;
            padding: 0;
            cursor: pointer;
            background-color: white;
            background-image: url('${closeButton}');
            background-size: cover;
            z-index: 120;
            user-select: none;
          }

          .close:hover,
          .close:focus {
            background-color: ${colors.G1};
            border-radius: 4px;
          }

          .escape-announcement {
            opacity: 0;
            width: 0;
            height: 0;
          }
        `}
      </style>
    </div>
  );
}
